/**
 * Navigation Configuration
 *
 * Single source of truth for application navigation structure.
 * Used by both mobile (MobileNav) and desktop (DesktopNav) navigation components
 * to ensure consistent grouping and prevent drift.
 */

import { School, Calendar, Users, ClipboardCheck, BarChart3, MapPin, Users2, HelpCircle, MessageSquare } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Navigation link definition
 */
export interface NavLink {
  to: string; // Route path (e.g., '/admin/school-years')
  label: string; // Display text (e.g., 'School Years')
  requiresModule?: 'attendance' | 'parentPortal' | 'sms' | 'messaging'; // Optional: Only show if module is enabled
  badge?: number; // Optional: Badge count (e.g., unread messages)
}

/**
 * Navigation group definition
 */
export interface NavGroup {
  title: string; // Group name (e.g., 'Setup & Configuration')
  icon?: LucideIcon; // Lucide icon component (optional)
  links: NavLink[]; // Child links in this group
  requiresModule?: 'attendance' | 'parentPortal' | 'sms' | 'messaging'; // Optional: Only show if module is enabled
  badge?: number; // Optional: Badge count on the group header (e.g., total unread messages)
}

/**
 * ADMIN NAVIGATION CONFIGURATION
 *
 * Defines the complete navigation structure for admin users.
 * All routes must have corresponding definitions in App.tsx.
 *
 * Organization:
 * 1. School Setup - Core configuration, calendar, behavior categories, location types (7 items)
 * 2. Scheduling - Schedule structure and assignments (7 items)
 * 3. People - Students, staff, and parent management (2-4 items)
 * 4. Attendance - Attendance records and reports (2 items, requires attendance module)
 * 5. Reports - Analytics and audit logs (5 items)
 * 6. Help & Support - Help center (1 item)
 */
export const adminNavGroups: NavGroup[] = [
  {
    title: 'School Setup',
    icon: School,
    links: [
      { to: '/admin/configuration', label: 'School Configuration' },
      { to: '/admin/school-years', label: 'School Years' },
      { to: '/admin/day-types', label: 'Day Types' },
      { to: '/admin/time-slots', label: 'Time Slots' },
      { to: '/admin/calendar', label: 'Calendar' },
      { to: '/admin/behavior-categories', label: 'Behavior Categories' },
      { to: '/admin/location-types', label: 'Location Types' },
    ],
  },
  {
    title: 'Scheduling',
    icon: Calendar,
    links: [
      { to: '/admin/periods', label: 'Periods' },
      { to: '/admin/blocks', label: 'Blocks' },
      { to: '/admin/classes', label: 'Classes' },
      { to: '/admin/staff-assignments', label: 'Staff Assignments' },
      { to: '/admin/bulk-meeting-patterns', label: 'Bulk Meeting Patterns' },
      { to: '/admin/lunch-groups', label: 'Lunch Groups' },
      { to: '/admin/lunch-schedules', label: 'Lunch Schedules' },
    ],
  },
  {
    title: 'People',
    icon: Users,
    links: [
      { to: '/admin/students', label: 'Students' },
      { to: '/admin/staff', label: 'Staff' },
      { to: '/admin/parents', label: 'Family Members', requiresModule: 'parentPortal' },
      { to: '/admin/bulk-qr-codes', label: 'Bulk QR Codes', requiresModule: 'parentPortal' },
    ],
  },
  {
    title: 'Messages',
    icon: MessageSquare,
    requiresModule: 'messaging',
    links: [
      { to: '/admin/messages?tab=inbox', label: 'Inbox' },
      { to: '/admin/messages?tab=updates', label: 'Updates' },
      { to: '/admin/messages?tab=sent', label: 'Sent' },
    ],
  },
  {
    title: 'Attendance',
    icon: ClipboardCheck,
    requiresModule: 'attendance',
    links: [
      { to: '/admin/attendance', label: 'Attendance Records' },
      { to: '/admin/reports/attendance', label: 'Attendance Report' },
    ],
  },
  {
    title: 'Reports',
    icon: BarChart3,
    links: [
      { to: '/admin/reports', label: 'Reports Dashboard' },
      { to: '/admin/reports/behavior-summary', label: 'Behavior Summary' },
      { to: '/admin/reports/staff-behavior-leaderboard', label: 'Staff Leaderboard' },
      { to: '/admin/reports/student-progress', label: 'Student Progress' },
      { to: '/admin/audit-logs', label: 'Audit Logs' },
      { to: '/admin/sms-usage', label: 'SMS Usage', requiresModule: 'sms' },
    ],
  },
  {
    title: 'Help & Support',
    icon: HelpCircle,
    links: [{ to: '/help', label: 'Help Center' }],
  },
];

// Re-export icons used for inline nav groups (Layout.tsx, TeacherLayout.tsx)
export { MapPin, Users2, HelpCircle, MessageSquare };

/**
 * Helper: Check if any link in a group matches the current path
 *
 * @param group - Navigation group to check
 * @param currentPath - Current route path (from useLocation)
 * @returns true if any link in the group matches currentPath
 *
 * @example
 * const location = useLocation();
 * const isActive = isGroupActive(group, location.pathname);
 */
export const isGroupActive = (group: NavGroup, currentPath: string): boolean => {
  return group.links.some((link) => {
    const linkPath = link.to.split('?')[0];
    return currentPath === linkPath || currentPath.startsWith(linkPath + '/');
  });
};

/**
 * Helper: Get flat list of all admin navigation links
 *
 * Useful for:
 * - Route validation (ensuring all nav links have routes)
 * - Testing (verifying all links are accessible)
 * - Debugging (listing all configured routes)
 *
 * @returns Array of all NavLink objects across all groups
 *
 * @example
 * const allLinks = getAllAdminLinks();
 * console.log(`Total admin routes: ${allLinks.length}`);
 */
export const getAllAdminLinks = (): NavLink[] => {
  return adminNavGroups.flatMap((group) => group.links);
};

/**
 * Helper: Find which group contains the current path
 *
 * @param currentPath - Current route path (from useLocation)
 * @returns NavGroup containing the active link, or undefined if no match
 *
 * @example
 * const location = useLocation();
 * const activeGroup = getActiveGroup(location.pathname);
 * if (activeGroup) {
 *   console.log(`Current section: ${activeGroup.title}`);
 * }
 */
export const getActiveGroup = (currentPath: string): NavGroup | undefined => {
  return adminNavGroups.find((group) => isGroupActive(group, currentPath));
};

/**
 * Helper: Get filtered scheduling links based on UI mode and lunch visibility
 *
 * @param mode - Scheduling UI mode ('traditional', 'elementary', or 'both')
 * @param showLunchGroups - Whether to show lunch groups and schedules links (default: true)
 * @returns Filtered array of scheduling navigation links
 *
 * @example
 * const links = getSchedulingLinks('traditional');
 * // Returns only Periods-related links (including lunch if showLunchGroups is true)
 */
export const getSchedulingLinks = (
  mode: 'traditional' | 'elementary' | 'both',
  showLunchGroups: boolean = true
): NavLink[] => {
  const allSchedulingLinks = [
    { to: '/admin/periods', label: 'Periods' },
    { to: '/admin/blocks', label: 'Blocks' },
    { to: '/admin/classes', label: 'Classes' },
    { to: '/admin/staff-assignments', label: 'Staff Assignments' },
    { to: '/admin/bulk-meeting-patterns', label: 'Bulk Meeting Patterns' },
    { to: '/admin/lunch-groups', label: 'Lunch Groups' },
    { to: '/admin/lunch-schedules', label: 'Lunch Schedules' },
  ];

  let links: NavLink[];

  if (mode === 'traditional') {
    // Traditional mode: Periods, Staff Assignments, Lunch Groups, Lunch Schedules
    links = allSchedulingLinks.filter((link) =>
      ['/admin/periods', '/admin/staff-assignments', '/admin/lunch-groups', '/admin/lunch-schedules'].includes(link.to)
    );
  } else if (mode === 'elementary') {
    // Elementary mode: Classes, Blocks, Staff Assignments, Bulk Meeting Patterns, Lunch Groups, Lunch Schedules
    links = allSchedulingLinks.filter((link) => link.to !== '/admin/periods');
  } else {
    // Both mode: Show all scheduling links
    links = allSchedulingLinks;
  }

  // Filter out lunch links if showLunchGroups is false
  if (!showLunchGroups) {
    links = links.filter((link) => !link.to.includes('lunch-groups') && !link.to.includes('lunch-schedules'));
  }

  return links;
};

/**
 * Helper: Get filtered admin navigation groups based on UI mode and lunch visibility
 *
 * Module-based filtering (attendance, parentPortal, messaging) is handled automatically
 * by DesktopDrawer/MobileNav using the `requiresModule` property on links and groups.
 *
 * @param mode - Scheduling UI mode ('traditional', 'elementary', or 'both')
 * @param showLunchGroups - Whether to show lunch groups and schedules links (default: true)
 * @returns Filtered admin navigation groups with scheduling links filtered by mode
 *
 * @example
 * const { tenant } = useAuthStore();
 * const mode = tenant?.settings?.schedulingUiMode || 'both';
 * const showLunch = tenant?.settings?.showLunchGroups !== false;
 * const navGroups = getAdminNavGroups(mode, showLunch);
 */
export const getAdminNavGroups = (
  mode: 'traditional' | 'elementary' | 'both',
  showLunchGroups: boolean = true
): NavGroup[] => {
  return adminNavGroups.map((group) => {
    if (group.title === 'Scheduling') {
      return {
        ...group,
        links: getSchedulingLinks(mode, showLunchGroups),
      };
    }
    return group;
  });
};

/**
 * DEVELOPMENT MODE VALIDATION
 *
 * Validates navigation configuration at runtime in development mode.
 * Catches common mistakes like:
 * - Routes not following /admin/* pattern
 * - Duplicate route paths
 * - Missing or invalid data
 */
if (process.env.NODE_ENV === 'development') {
  const allLinks = getAllAdminLinks();

  // Validation: Ensure all routes follow /admin/* pattern (except /help which is cross-cutting)
  allLinks.forEach((link) => {
    const linkPath = link.to.split('?')[0];
    if (!linkPath.startsWith('/admin/') && linkPath !== '/help') {
      console.warn(`[Navigation] Route doesn't start with /admin/:`, link);
    }

    if (!link.label || link.label.trim() === '') {
      console.warn(`[Navigation] Empty label found for route:`, link.to);
    }
  });

  // Validation: Check for duplicate routes (use full path including query params)
  const paths = allLinks.map((l) => l.to);
  const duplicates = paths.filter((path, index) => paths.indexOf(path) !== index);
  if (duplicates.length > 0) {
    console.error('[Navigation] Duplicate routes found:', [...new Set(duplicates)]);
  }

  // Validation: Check for empty groups
  adminNavGroups.forEach((group) => {
    if (group.links.length === 0) {
      console.warn(`[Navigation] Empty group found:`, group.title);
    }

    if (!group.title || group.title.trim() === '') {
      console.warn(`[Navigation] Group with empty title found`);
    }
  });

  // Info: Log navigation structure summary
  console.log('[Navigation] Configuration loaded:', {
    totalGroups: adminNavGroups.length,
    totalLinks: allLinks.length,
    groups: adminNavGroups.map((g) => ({
      title: g.title,
      linkCount: g.links.length,
    })),
  });
}
